/**
 * MessageHandler - Core message handling for preview runtime
 *
 * Handles all postMessage communication between admin panel and runtime.
 * Implements origin validation, message routing, and error handling.
 *
 * IMPORTANT: The admin panel sends FLAT messages like:
 *   { type: 'full-update', data: {...}, sections: {...} }
 * NOT wrapped messages like { type, payload, timestamp }.
 * All methods are designed to accept and send flat messages.
 */

const VALID_INCOMING_TYPES = [
  'full-update',
  'field-update',
  'section-toggle',
  'style-update',
  'viewport-change',
  'section-highlight',
  'scroll-to-section',
];

/**
 * Configuration for MessageHandler
 */
interface MessageHandlerConfig {
  /** Enable debug logging */
  debug: boolean;
}

const DEFAULT_CONFIG: MessageHandlerConfig = {
  debug: false,
};

export class MessageHandler {
  private config: MessageHandlerConfig;
  private messageHandlers: Map<string, (msg: any) => void> = new Map();
  private messageHistory: Array<{ message: any; timestamp: number; direction: 'incoming' | 'outgoing' }> = [];
  private maxHistorySize = 100;
  private isInitialized = false;
  private boundHandleMessage: (event: MessageEvent) => void;
  private _parentOrigin: string = '*';

  constructor(config: Partial<MessageHandlerConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.boundHandleMessage = this.handleMessage.bind(this);
  }

  /**
   * Initialize message handling
   */
  init(): void {
    if (this.isInitialized) {
      this.log('MessageHandler already initialized');
      return;
    }

    window.addEventListener('message', this.boundHandleMessage);
    this.isInitialized = true;
    this.log('MessageHandler initialized');
  }

  /**
   * Handle incoming postMessage events.
   * Origin validation matches the current runtime's permissive approach:
   * - Same origin
   * - localhost / 127.0.0.1
   * - *.pages.dev
   * - Any https://
   */
  handleMessage(event: MessageEvent): void {
    try {
      const origin = event.origin;

      if (!this.validateOrigin(origin)) {
        this.log(`Rejected message from invalid origin: ${origin}`);
        return;
      }

      // Latch parent origin on first valid message (for sendMessage targeting)
      if (this._parentOrigin === '*') {
        this._parentOrigin = origin;
      }

      const msg = event.data;

      // Validate: must be an object with a string `type`
      if (!msg || typeof msg !== 'object' || typeof msg.type !== 'string') {
        return;
      }

      // Must be a known incoming message type
      if (!VALID_INCOMING_TYPES.includes(msg.type)) {
        return;
      }

      this.log('Received message:', msg.type);
      this.recordMessage(msg, 'incoming');

      // Route the FULL flat message to the handler (not msg.payload)
      const handler = this.messageHandlers.get(msg.type);
      if (handler) {
        handler(msg);
      } else {
        this.log(`No handler registered for message type: ${msg.type}`);
      }
    } catch (error) {
      console.error('MessageHandler: Error handling message', error);
    }
  }

  /**
   * Send a flat message to parent window.
   * Accepts a full message object and sends it as-is.
   */
  sendToParent(message: Record<string, any>): void {
    try {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage(message, this._parentOrigin);
        this.log('Sent message:', message.type);
        this.recordMessage(message, 'outgoing');
      }
    } catch (error) {
      console.error('MessageHandler: Error sending message', error);
    }
  }

  /**
   * Validate message origin.
   * Matches the current runtime's permissive approach.
   */
  validateOrigin(origin: string): boolean {
    if (origin === window.location.origin) return true;
    if (origin.startsWith('http://localhost') || origin.startsWith('http://127.0.0.1')) return true;
    if (origin.startsWith('https://localhost') || origin.startsWith('https://127.0.0.1')) return true;
    if (origin.endsWith('.pages.dev')) return true;
    if (origin.startsWith('https://')) return true;
    return false;
  }

  /**
   * Register a message handler for a specific message type.
   * Handler receives the FULL flat message object.
   */
  registerHandler(type: string, handler: (msg: any) => void): void {
    this.messageHandlers.set(type, handler);
    this.log(`Registered handler for message type: ${type}`);
  }

  /**
   * Unregister a message handler
   */
  unregisterHandler(type: string): void {
    this.messageHandlers.delete(type);
    this.log(`Unregistered handler for message type: ${type}`);
  }

  /**
   * Get the latched parent origin
   */
  getParentOrigin(): string {
    return this._parentOrigin;
  }

  /**
   * Destroy message handler and clean up resources
   */
  destroy(): void {
    window.removeEventListener('message', this.boundHandleMessage);
    this.messageHandlers.clear();
    this.isInitialized = false;
    this.log('MessageHandler destroyed');
  }

  enableDebug(): void {
    this.config.debug = true;
  }

  disableDebug(): void {
    this.config.debug = false;
  }

  /**
   * Record message in history for debugging
   */
  private recordMessage(message: any, direction: 'incoming' | 'outgoing'): void {
    this.messageHistory.push({
      message: { ...message },
      timestamp: Date.now(),
      direction,
    });
    if (this.messageHistory.length > this.maxHistorySize) {
      this.messageHistory.shift();
    }
  }

  getMessageHistory(): Array<{ message: any; timestamp: number; direction: 'incoming' | 'outgoing' }> {
    return [...this.messageHistory];
  }

  clearMessageHistory(): void {
    this.messageHistory = [];
  }

  private log(message: string, ...args: any[]): void {
    if (this.config.debug) {
      console.log(`[MessageHandler] ${message}`, ...args);
    }
  }
}
